import { mdiStarOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { USER_SETTING, getCheckTypeFromSlug } from '@solid/shared';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/auth.context';
import { useKirbyCheckGroups, useKirbyChecks } from '../../hooks/useKirbyData';
import { useSetting } from '../../hooks/useSetting';
import { canUseFavorites } from '../../services/setting.service';
import Layout from '../global/Layout';
import CheckGroup from './CheckGroup';
import * as Styled from './HomePage.styles';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Virtual slug for favorites
const FAVORITES_SLUG = 'favoriten';
const ORGANIZATION_SLUG = 'organisation';

const HomePage: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const firstTimeUser = auth.user.lastActive === null;
  const { data: checkGroups } = useKirbyCheckGroups();
  const { data: kirbyChecks } = useKirbyChecks();
  const [favorites, setFavorites] = useSetting(USER_SETTING.CHECK_FAVORITES);
  const { t } = useTranslation();

  const [openGroups, setOpenGroups] = useState([]);
  const l = useLocation();

  const handleUrlChange = () => {
    const urlHash = window.location.hash.replace('#', '');
    const value = [];
    if (urlHash) {
      const foundGroup = checkGroups?.find(group => group.slug === urlHash);
      if (foundGroup) {
        value.push(foundGroup.slug);
      }
    }
    if (favorites?.length > 0) {
      value.push(FAVORITES_SLUG);
    }
    if (value.length === 0) {
      value.push(checkGroups?.[0].slug ?? null);
    }
    setOpenGroups(value);
  };

  useEffect(() => {
    setTimeout(handleUrlChange, 500);
  }, [l.hash]);

  const favoriteChecks = kirbyChecks?.filter(check => favorites?.includes(getCheckTypeFromSlug(check.slug)));
  const orgChecks = kirbyChecks?.filter(check => check.isOrgExclusive === true);

  const toggleGroup = (slug: string) => {
    if (openGroups.includes(slug)) {
      setOpenGroups(openGroups.filter(s => s !== slug));
    } else {
      setOpenGroups([...openGroups, slug]);
    }
  };

  const groups = [
    ...(canUseFavorites(auth.user.plan)
      ? [{ name: t('pages.home.favorites'), slug: FAVORITES_SLUG, checks: favoriteChecks, flag: 'favorites' }]
      : []),
    ...(orgChecks?.length > 0
      ? [{ name: t('pages.home.individualChecks'), slug: ORGANIZATION_SLUG, checks: orgChecks, flag: 'organization' }]
      : []),
    ...(checkGroups ?? []),
  ];

  return (
    <Layout showSidebar>
      <div>
        <Styled.Headline>
          <Trans
            i18nKey={firstTimeUser ? 'pages.home.titleFirstTimeUser' : 'pages.home.titleRegularUser'}
            values={{ user: `${auth.user.firstName} ${auth.user.lastName}` }}
            components={{ br: <br />, span: <span /> }}
          />
        </Styled.Headline>

        <Styled.SubHeadline>{t('pages.home.subtitle')}</Styled.SubHeadline>

        {groups.map(group => (
          <CheckGroup
            key={group.slug}
            isOpen={openGroups.includes(group.slug)}
            onClick={() => toggleGroup(group.slug)}
            group={group}
            favorites={favorites ?? []}
            onFavoriteStateChange={checkType => {
              if ((favorites ?? []).includes(checkType)) {
                setFavorites(favorites.filter(f => f !== checkType));
              } else {
                setFavorites([...(favorites ?? []), checkType]);
              }
            }}
            emptyState={
              group.slug === FAVORITES_SLUG ? (
                <Styled.FavoritesEmptyState>
                  <Trans
                    i18nKey='pages.home.favoritesEmptyState'
                    components={{ br: <br />, Icon: <Icon size='1em' path={mdiStarOutline} /> }}
                  />
                </Styled.FavoritesEmptyState>
              ) : null
            }
          />
        ))}

        <Styled.FeedbackContainer>
          <h4>
            <Trans
              i18nKey='pages.home.feedback'
              components={{
                br: <br />,
                a: <a href='https://aebcom.typeform.com/to/juAcK9' target='_blank' rel='noopener noreferrer' />,
              }}
            />
          </h4>
        </Styled.FeedbackContainer>
      </div>
    </Layout>
  );
};

export default HomePage;
